import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { AdminUser, AdminAuthResponse } from '../types/admin';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AdminDashboard: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Create user dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);

  // The password is never compared in the browser and never stored here:
  // anything in this bundle is readable by every visitor. The server checks
  // it and returns a short-lived token, which every admin call must carry.
  const [adminToken, setAdminToken] = useState<string | null>(null);

  const authHeaders = (token: string): HeadersInit => ({
    Authorization: `Bearer ${token}`,
  });

  const handleSessionExpired = () => {
    setAdminToken(null);
    setIsAuthenticated(false);
    setUsers([]);
    setError('Session administrateur expirée. Reconnectez-vous.');
  };

  const handleLogin = async () => {
    if (!password) {
      setError('Mot de passe requis');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('password', password);

      const response = await fetch(`${API_BASE_URL}/api/admin/auth`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data: AdminAuthResponse = await response.json();
        if (data.authenticated && data.access_token) {
          setAdminToken(data.access_token);
          setIsAuthenticated(true);
          // The password is not kept once it has been exchanged.
          setPassword('');
          fetchUsers(data.access_token);
        } else {
          setError('Authentication failed');
        }
      } else if (response.status === 429) {
        setError('Trop de tentatives. Réessayez dans quelques minutes.');
      } else if (response.status === 503) {
        setError("L'API d'administration est désactivée sur ce serveur.");
      } else {
        setError('Mot de passe administrateur invalide');
      }
    } catch (err) {
      setError('Network error during authentication');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (token?: string) => {
    const active = token ?? adminToken;
    if (!active) {
      handleSessionExpired();
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        headers: authHeaders(active),
      });
      if (response.status === 401) {
        handleSessionExpired();
      } else if (response.ok) {
        const data: AdminUser[] = await response.json();
        setUsers(data);
      } else {
        setError('Failed to fetch users');
      }
    } catch (err) {
      setError('Network error while fetching users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUsername.trim() || !newPassword.trim()) {
      setError('Username and password are required');
      return;
    }

    setCreatingUser(true);
    try {
      const formData = new FormData();
      formData.append('username', newUsername.trim());
      formData.append('password', newPassword);

      if (!adminToken) {
        handleSessionExpired();
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        method: 'POST',
        body: formData,
        headers: authHeaders(adminToken),
      });

      if (response.status === 401) {
        handleSessionExpired();
      } else if (response.ok) {
        const newUser: AdminUser = await response.json();
        setUsers([...users, newUser]);
        setSuccess(`User "${newUser.username}" created successfully`);
        setCreateDialogOpen(false);
        setNewUsername('');
        setNewPassword('');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create user');
      }
    } catch (err) {
      setError('Network error while creating user');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteUser = async (userId: number, username: string) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      return;
    }

    try {
      if (!adminToken) {
        handleSessionExpired();
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: authHeaders(adminToken),
      });

      if (response.status === 401) {
        handleSessionExpired();
      } else if (response.ok) {
        setUsers(users.filter(user => user.id !== userId));
        setSuccess(`User "${username}" deleted successfully`);
      } else {
        setError('Failed to delete user');
      }
    } catch (err) {
      setError('Network error while deleting user');
    }
  };

  const handleLogout = () => {
    setAdminToken(null);
    setIsAuthenticated(false);
    setPassword('');
    setUsers([]);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 4,
            maxWidth: 400,
            width: '100%',
            mx: 2,
            borderRadius: 2,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Accès administrateur
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Entrez le mot de passe administrateur pour continuer
            </Typography>
          </Box>

          <TextField
            fullWidth
            type="password"
            label="Mot de passe administrateur"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
            sx={{ mb: 3 }}
            autoFocus
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleLogin}
            disabled={loading}
            sx={{ mb: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Accéder au tableau de bord'}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={() => window.history.back()}
          >
            ← Retour à l’application
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PeopleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1">
                Admin Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                User Management System
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => fetchUsers()}
              disabled={loading}
            >
              Rafraîchir
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
            >
              Déconnexion
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="primary">
                {users.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                 Utilisateurs totaux
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="success.main">
                {users.filter(u => new Date(u.creation_date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                 Nouveaux ce mois-ci
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Users Table */}
      <Paper elevation={2}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Utilisateurs</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
               Ajouter un utilisateur
            </Button>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                 <TableCell>Nom d’utilisateur</TableCell>
                 <TableCell>Création</TableCell>
                 <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                     <Typography variant="body2" color="text.secondary">
                       Aucun utilisateur trouvé
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.id}</TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {user.username}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatDate(user.creation_date)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete user">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteUser(user.id, user.username)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Créer un utilisateur</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nom d’utilisateur"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            autoFocus
          />
          <TextField
            fullWidth
            type="password"
            label="Mot de passe"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Annuler</Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={creatingUser || !newUsername.trim() || !newPassword.trim()}
          >
            {creatingUser ? <CircularProgress size={20} /> : 'Créer l’utilisateur'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboard; 